/**
 * PipelineDetailShell — shared chrome for the three bubble-detail panels
 * (Industry / Use Case / Asset) rendered below IndustryPipelineOverview.
 *
 * Owns the parts the three panels always duplicated: header + 4×2 KPI grid +
 * StageBreakdown + loading / error state. The GetUcPipelineDetail fetch also
 * lives here since the request shape is a single (industry, ucName?, assetId?)
 * struct — each caller passes whichever subset it needs.
 *
 * Callers own:
 *   - the identity render (title text / subtitle / badge — passed as children
 *     of a `header` render prop),
 *   - the KPI values (each panel derives them differently: UC reads bubble
 *     props directly, Industry / Asset roll up from stageInsight).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Popover, Spinner } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetUcPipelineDetailCommand,
  type GetUcPipelineDetailOutput,
  type PipelineOpp,
  type StageGroup,
} from '../../../client/IvtHubClient';
import StageBreakdown from './StageBreakdown';

/**
 * Formats the "Launch Ratio" KPI value — launchedArr as a fraction of totalArr.
 * "—" when there is no ARR to divide by (empty scope). Every panel
 * (Industry / Use Case / Asset) has both fields on its bubble.
 */
export function launchRatioLabel(launchedArr: number, totalArr: number): string {
  if (totalArr <= 0) return '—';
  const pct = Math.round((launchedArr / totalArr) * 100);
  return `${pct}%`;
}

/** Info tooltips reused by every panel — the semantics are identical. */
export const KPI_INFO_TIPS: Record<string, string> = {
  pipelineDepth:
    'Pipeline 平均参与深度：每个 Opp 上最强 IVT 相关活动得分的平均值（Σ 每 Opp 的 max_score ÷ Opp 数），与气泡图横坐标口径一致。数值越高，IVT 资产被真正谈到、演示、部署的深度越大。',
  penetration:
    '渗透率：至少被一次 IVT 相关活动触达的 Opp 占比。反映资产在客户群的覆盖面。',
  stronglyTouchedOpps:
    '强相关opps数量：至少有一条 LLM score=3 强匹配活动的 Opp 数量。反映资产被深度谈到、演示或部署的客户覆盖广度。',
  touchedOpps:
    'IVT相关opps数量：至少被一次 IVT 相关活动（score≥1）触达的 Opp 数量，等于渗透率的分子。',
};

/** One KPI cell — label + optional info popover + value. Exposed so callers
 *  can compose their own 4×2 grid without duplicating the popover code. */
export function StatCell({
  label,
  value,
  info,
}: {
  label: string;
  value: string | number;
  info?: string;
}) {
  return (
    <div className="ivt-ucdp__kpi">
      <div className="ivt-ucdp__kpi-label">
        {label}
        {info && (
          <Popover
            position="top"
            size="small"
            triggerType="custom"
            content={<span>{info}</span>}
          >
            <button type="button" className="ivt-ucdp__info-btn" aria-label={`${label} 说明`}>
              ⓘ
            </button>
          </Popover>
        )}
      </div>
      <div className="ivt-ucdp__kpi-value">{value}</div>
    </div>
  );
}

/** stageInsight roll-up shape. Stage-scoped panels (Industry / Asset / UC)
 *  source pipelineDepth from their bubble's influenceDensityOpp — so it
 *  matches the bubble chart X-axis exactly — and leave the field unset here.
 *  Opp-scoped panels (Account / Ghost) can't use the bubble value (it's not
 *  filtered to their sub-scope), so deriveKpisFromOpps computes it locally.
 *  Returns em-dashes when nothing has been scored yet. */
export interface DerivedKpis {
  stronglyTouchedOpps: number | string;
  touchedOpps: number | string;
  penetrationPct: number | string;
  /** Only populated by {@link deriveKpisFromOpps}; stage-scoped panels
   *  read this off their bubble instead. */
  pipelineDepth?: number | string;
}

/**
 * Roll influence KPIs up from a raw opp list (Account panel uses this after
 * client-side filtering an industry-level fetch by accountName — stageInsight
 * is industry-scoped and won't reflect the per-account subset). Same shape as
 * {@link deriveKpisFromStages}; the two are intentionally interchangeable at
 * the call site.
 */
export function deriveKpisFromOpps(opps: PipelineOpp[]): DerivedKpis {
  if (opps.length === 0) {
    return { stronglyTouchedOpps: 0, touchedOpps: 0, penetrationPct: '0%', pipelineDepth: '0.00' };
  }
  let stronglyTouched = 0;
  let touched = 0;
  let maxScoreSum = 0;
  for (const opp of opps) {
    const acts = opp.activities ?? [];
    let hasScored = false;
    let hasStrong = false;
    let maxScore = 0;
    for (const a of acts) {
      const s = a.relevanceScore ?? 0;
      if (s >= 1) hasScored = true;
      if (s >= 3) hasStrong = true;
      if (s > maxScore) maxScore = s;
    }
    if (hasScored) touched += 1;
    if (hasStrong) stronglyTouched += 1;
    maxScoreSum += maxScore;
  }
  const total = opps.length;
  const penetration = total > 0 ? Math.round((touched / total) * 100) : 0;
  // Σ per-opp max_score ÷ opp count — same formula the backend uses for
  // bubble.influenceDensityOpp, so the drawer matches the bubble chart X-axis.
  const depth = total > 0 ? (maxScoreSum / total).toFixed(2) : '0.00';
  return {
    stronglyTouchedOpps: stronglyTouched,
    touchedOpps: touched,
    penetrationPct: `${penetration}%`,
    pipelineDepth: depth,
  };
}

/** Top-half KPIs (Opps / ARR / Launch Ratio) derived from the detail
 *  response so they refresh in lockstep with the filter, instead of from a
 *  stale bubble snapshot captured at click time. `oppFilter` narrows to the
 *  same opp subset StageBreakdown renders (Account / Ghost panels use it).
 *  Returns null while the detail response is still loading — callers render
 *  em-dash placeholders in that case. */
export interface TopKpis {
  oppCount: number;
  totalArr: number;
  launchedArr: number;
}
export function deriveTopKpisFromDetail(
  detail: GetUcPipelineDetailOutput | null,
  oppFilter?: (opp: PipelineOpp) => boolean,
): TopKpis | null {
  if (!detail) return null;
  let oppCount = 0;
  let totalArr = 0;
  let launchedArr = 0;
  for (const stage of detail.stages ?? []) {
    for (const opp of stage.opps ?? []) {
      if (oppFilter && !oppFilter(opp)) continue;
      oppCount += 1;
      const arr = opp.totalArr ?? 0;
      totalArr += arr;
      if (opp.oppStatus === 'launched') launchedArr += arr;
    }
  }
  return { oppCount, totalArr, launchedArr };
}

export function deriveKpisFromStages(stages: StageGroup[] | undefined): DerivedKpis {
  if (!stages) {
    return { stronglyTouchedOpps: '—', touchedOpps: '—', penetrationPct: '—' };
  }
  let totalStronglyTouched = 0;
  let totalOppsTouched = 0;
  let totalOppsTotal = 0;
  for (const stage of stages) {
    const si = stage.stageInsight;
    if (!si) continue;
    totalStronglyTouched += si.oppsStronglyTouched ?? 0;
    totalOppsTouched += si.oppsTouched ?? 0;
    totalOppsTotal += si.oppsTotal ?? 0;
  }
  const penetration =
    totalOppsTotal > 0 ? Math.round((totalOppsTouched / totalOppsTotal) * 100) : 0;
  return {
    stronglyTouchedOpps: totalStronglyTouched,
    touchedOpps: totalOppsTouched,
    penetrationPct: `${penetration}%`,
  };
}

interface FetchArgs {
  industry: string;
  ucName?: string;
  assetId?: string;
}

/**
 * The shared shell. Renders header + KPI grid + fetch-driven StageBreakdown.
 * `renderKpis` receives the fetched detail (or null while loading) so callers
 * that derive KPIs from stageInsight can hook into the same state without
 * running their own fetch.
 */
export function PipelineDetailShell({
  title,
  subtitle,
  headerExtra,
  fetch,
  pipelineTypeFilter,
  snapshotDate,
  oppFilter,
  showUcNameInStages,
  loadingLabel,
  errorHeader,
  onClose,
  renderKpis,
  pipelineFilter,
}: {
  title: string;
  /** Small line under the title (industry, UC name, etc). */
  subtitle?: string;
  /** Extra header content next to the title — e.g. the UC priority badge. */
  headerExtra?: ReactNode;
  fetch: FetchArgs;
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded to GetUcPipelineDetail so the drill-in
   *  returns the same opp set the bubble that launched it aggregated over.
   *  Undefined = no server-side filter (full population under industry+ucName). */
  pipelineFilter?: string;
  /** ISO YYYY-MM-DD passed through to GetUcPipelineDetail. Omit for live view.
   *  Matches whatever the chart above is displaying so the detail panel stays
   *  time-consistent with the bubble the user clicked. */
  snapshotDate?: string;
  /** Extra client-side opp predicate for scopes the backend doesn't yet
   *  support natively (Account View filters an industry-level fetch by
   *  accountName). Applied to StageBreakdown AND to the detail passed into
   *  renderKpis so stageInsight roll-ups reflect the scoped set. */
  oppFilter?: (opp: PipelineOpp) => boolean;
  /** true → StageBreakdown labels each opp with its UC (Industry / Asset views
   *  span multiple UCs); false → single-UC view, UC label hidden. */
  showUcNameInStages: boolean;
  /** e.g. "加载 FSI 行业明细…" / "加载 XYZ Asset 明细…" */
  loadingLabel: string;
  /** Alert header when the fetch fails. */
  errorHeader: string;
  onClose: () => void;
  /** Caller renders the 4×2 KPI grid. Detail is null while loading — callers
   *  that derive from stages should render em-dash placeholders in that case. */
  renderKpis: (detail: GetUcPipelineDetailOutput | null) => ReactNode;
}) {
  const [detail, setDetail] = useState<GetUcPipelineDetailOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    (async () => {
      try {
        const client = getIvtClient();
        const out = await client.send(
          new GetUcPipelineDetailCommand({
            industry: fetch.industry,
            ucName: fetch.ucName,
            assetId: fetch.assetId,
            // Cast: SDK types the enum as PipelineType — dropdown values are
            // the exact enum strings, so the cast is a no-op at runtime.
            pipelineType: pipelineTypeFilter as never,
            pipelineFilter,
            snapshotDate,
          }),
        );
        if (cancelled) return;
        setDetail(out);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetch.industry, fetch.ucName, fetch.assetId, pipelineTypeFilter, pipelineFilter, snapshotDate]);

  const kpisNode = useMemo(() => renderKpis(detail), [renderKpis, detail]);

  return (
    <div className="ivt-ucdp">
      <div className="ivt-ucdp__head">
        <div className="ivt-ucdp__title-block">
          <div className="ivt-ucdp__title">{title}</div>
          {subtitle && <div className="ivt-ucdp__subtitle">{subtitle}</div>}
          {headerExtra}
        </div>
        <button
          type="button"
          className="ivt-ucdp__close"
          aria-label="Close detail"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="ivt-ucdp__kpis">{kpisNode}</div>

      {loading && (
        <div className="ivt-ucdp__loading">
          <Spinner /> <span>{loadingLabel}</span>
        </div>
      )}
      {error && (
        <Alert type="error" header={errorHeader}>
          {error}
        </Alert>
      )}
      {!loading && !error && detail && (
        <StageBreakdown
          detail={detail}
          showUcName={showUcNameInStages}
          pipelineTypeFilter={pipelineTypeFilter}
          oppFilter={oppFilter}
        />
      )}
    </div>
  );
}
